import Dialog from './Dialog';
import React, { useState } from 'react';
import { ACHIEVEMENTS, CATEGORY_PALETTE, getEarnedAchievements } from '../achievements';

// Format a millisecond duration as a friendly "Xh Ym" / "Xm Ys" / "Xs" string.
const fmtDuration = (ms) => {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

// Response time reads nicely in seconds once it's past ~1s, otherwise ms.
const fmtReaction = (ms) => {
  if (ms == null) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
  return `${Math.round(ms)}ms`;
};

const Row = ({ label, session, lifetime }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-white/5">
    <span className="text-gray-400 text-[0.6875rem] uppercase tracking-widest">{label}</span>
    <span className="flex gap-3">
      <span className="text-white text-sm font-bold w-20 text-right">{session}</span>
      <span className="text-indigo-200 text-sm font-bold w-20 text-right">{lifetime}</span>
    </span>
  </div>
);

const MilestoneOrb = ({ category, unlocked = false, next = false, size = 52 }) => {
  const [from, to] = CATEGORY_PALETTE[category] || CATEGORY_PALETTE.firstSteps;
  return (
    <span
      aria-hidden="true"
      className={`relative inline-flex shrink-0 rounded-full ${next ? 'animate-pulse' : ''}`}
      style={{
        width: size,
        height: size,
        background: unlocked || next
          ? `radial-gradient(circle at 35% 30%, rgba(255,255,255,.9) 0 3%, ${from} 18%, ${to} 62%, rgba(15,23,42,.9) 100%)`
          : 'radial-gradient(circle at 35% 30%, rgba(255,255,255,.12), rgba(99,102,241,.08) 48%, rgba(15,23,42,.25) 72%)',
        border: unlocked || next ? '1px solid rgba(199,210,254,.38)' : '1px solid rgba(199,210,254,.12)',
        boxShadow: unlocked || next
          ? `0 0 ${Math.round(size * .65)}px ${Math.round(size * .12)}px ${from}35, inset 0 0 ${Math.round(size * .28)}px rgba(255,255,255,.12)`
          : 'inset 0 0 14px rgba(129,140,248,.06)',
      }}
    />
  );
};

const RhythmTrail = ({ days = 0 }) => (
  <div className="mt-3 flex items-center" aria-label={`${days} day current rhythm`}>
    {Array.from({ length: 7 }, (_, index) => {
      const active = index < Math.min(days, 7);
      return (
        <React.Fragment key={index}>
          {index > 0 && <span className={`h-px flex-1 ${active ? 'bg-indigo-300/35' : 'bg-white/[0.06]'}`} />}
          <span
            className="rounded-full"
            style={{
              width: active ? 10 : 7,
              height: active ? 10 : 7,
              background: active ? 'radial-gradient(circle at 35% 30%, #fff, #60a5fa 28%, #8b5cf6 72%)' : 'rgba(255,255,255,.08)',
              boxShadow: active ? '0 0 10px rgba(129,140,248,.75)' : 'none',
            }}
          />
        </React.Fragment>
      );
    })}
  </div>
);

const supportedProgressMetrics = new Set([
  'totalTaps', 'totalActiveMs', 'distinctUseDays', 'currentStreakDays',
  'completedRoutines', 'uniqueRoutinesCompleted',
  'checkInsCompleted', 'improvedCheckIns',
  'bestReactionMs', 'eliteReflexCount', 'bestEliteReflexStreak',
]);

// Most metrics are "bigger is better" (taps, minutes, streak days). Reaction
// time is the one "smaller is better" metric, so its progress runs the other
// way: 0% at `baseline` (an untrained reaction), 100% at/under the target.
const progressToward = (badge, life) => {
  const raw = life[badge.condition.metric];
  if (badge.condition.comparator === 'lte') {
    if (raw == null) return 0;
    const { value, baseline = value * 2 } = badge.condition;
    if (raw <= value) return 1;
    return Math.max(0, Math.min(1, (baseline - raw) / (baseline - value)));
  }
  const current = Number(raw ?? 0);
  return Math.min(1, current / badge.condition.value);
};

const StatsScreen = ({ stats, hideProgress = false, onSetProgressVisible, onResetSession, onResetLifetime, onClose }) => {
  const [showAllMilestones, setShowAllMilestones] = useState(false);
  const rawLife = (stats && stats.lifetime) || {};
  const life = { ...rawLife, uniqueRoutinesCompleted: (rawLife.completedRoutineIds || []).length };
  const sess = (stats && stats.session) || {};
  const avg = (sumMs, n) => (n > 0 ? sumMs / n : null);
  const earned = getEarnedAchievements(life);
  const earnedIds = new Set(earned.map(({ id }) => id));
  const visibleBadges = ACHIEVEMENTS.filter(({ condition }) => supportedProgressMetrics.has(condition.metric));
  const nextMilestone = (() => {
    const remaining = visibleBadges.filter(({ id }) => !earnedIds.has(id));
    if (!remaining.length) return null;
    return remaining.reduce((closest, badge) => {
      const progress = progressToward(badge, life);
      return !closest || progress > closest.progress ? { badge, progress } : closest;
    }, null);
  })();
  const displayedBadges = showAllMilestones
    ? visibleBadges
    : visibleBadges.filter(({ id }) => earnedIds.has(id)).slice(-4);

  return (
    <Dialog onClose={onClose} label="Your moments" className="fixed inset-0 z-modal bg-black/80 backdrop-blur-md flex items-center justify-center p-4 pointer-events-auto">
      <div
        className="max-w-md w-full bg-gradient-to-b from-slate-900 to-[#10172a] border border-indigo-300/15 rounded-3xl p-6 shadow-2xl max-h-[85vh] overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
      >
        <h2 className="text-2xl font-bold text-white mb-1">Your moments</h2>
        <p className="text-indigo-100/55 text-sm mb-5">A quiet record of time you have made for yourself.</p>

        {hideProgress ? (
          <div className="py-9 px-5 rounded-2xl bg-white/5 border border-white/10 text-center">
            <div className="text-3xl mb-3">◌</div>
            <p className="text-white font-semibold">Progress is hidden</p>
            <p className="text-gray-400 text-sm mt-2 leading-relaxed">Your practice still belongs to you. Show it again whenever it feels helpful.</p>
            <button onClick={() => onSetProgressVisible?.(true)} className="mt-5 px-4 py-2 rounded-full bg-indigo-500/20 text-indigo-200 text-sm font-semibold">Show my progress</button>
          </div>
        ) : (
          <>

        <div className="grid grid-cols-2 gap-2 mb-5">
          <div className="rounded-2xl bg-sky-400/[0.07] border border-sky-300/10 p-4">
            <p className="text-[0.625rem] uppercase tracking-widest text-sky-200/60">Your rhythm now</p>
            <p className="text-2xl font-bold text-white mt-1">{life.currentStreakDays ?? 0} <span className="text-sm text-gray-400">days</span></p>
            <RhythmTrail days={life.currentStreakDays ?? 0} />
          </div>
          <div className="rounded-2xl bg-violet-400/[0.08] border border-violet-300/15 p-4">
            <p className="text-[0.625rem] uppercase tracking-widest text-violet-200/65">Longest rhythm</p>
            <p className="text-2xl font-bold text-indigo-100 mt-1">{life.longestStreakDays ?? 0} <span className="text-sm text-indigo-300/70">days</span></p>
          </div>
        </div>

        <p className="-mt-2 mb-5 text-[0.6875rem] leading-relaxed text-white/65">A break never removes what you have already done.</p>

        {/* Column headers */}
        <div className="flex items-center justify-end gap-3 mb-1">
          <span className="text-gray-500 text-[0.625rem] uppercase tracking-widest w-20 text-right">Session</span>
          <span className="text-indigo-300 text-[0.625rem] uppercase tracking-widest w-20 text-right">All time</span>
        </div>

        <Row label="Taps" session={(sess.taps ?? 0).toLocaleString()} lifetime={(life.totalTaps ?? 0).toLocaleString()} />
        <Row label="Calm time" session={fmtDuration(sess.activeMs ?? 0)} lifetime={fmtDuration(life.totalActiveMs ?? 0)} />
        <Row label="Avg. response" session={fmtReaction(avg(sess.reactionMs, sess.reactionCount))} lifetime={fmtReaction(avg(life.totalReactionMs, life.reactionCount))} />
        <Row label="Best response" session={fmtReaction(sess.bestReactionMs)} lifetime={fmtReaction(life.bestReactionMs)} />
        <Row label="Sessions" session="—" lifetime={(life.totalSessions ?? 0).toLocaleString()} />

        <p className="text-gray-500 text-xs leading-relaxed mt-4">
          Response time is measured from the moment an orb appears to your tap, so it
          can never be quicker than your “Speed” setting. It’s a bit of fun — not a
          clinical measure.
        </p>

        <div className="border-t border-white/10 mt-6 pt-5">
          <div className="flex items-end justify-between gap-3 mb-3">
            <div><h3 className="text-white font-bold">Gentle milestones</h3><p className="text-indigo-100/40 text-xs mt-1">{earned.length === 0 ? 'Your first one is waiting whenever you are ready' : `${earned.length} moments recognised`}</p></div>
          </div>

          {nextMilestone && (
            <div className="mb-3 overflow-hidden rounded-3xl border border-indigo-300/20 bg-gradient-to-br from-sky-400/10 via-indigo-400/10 to-violet-400/10 p-4">
              <div className="flex items-center gap-3">
                <MilestoneOrb category={nextMilestone.badge.category} next size={58} />
                <div className="min-w-0 flex-1">
                  <p className="text-[0.625rem] uppercase tracking-[0.18em] text-indigo-200/60">A moment ahead</p>
                  <p className="mt-0.5 font-bold text-white">{nextMilestone.badge.title}</p>
                  <p className="mt-0.5 text-xs leading-snug text-indigo-100/55">{nextMilestone.badge.description}</p>
                </div>
              </div>
              <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-black/25">
                <div className="h-full rounded-full bg-gradient-to-r from-sky-400 to-violet-400 transition-all" style={{ width: `${Math.max(3, nextMilestone.progress * 100)}%` }} />
              </div>
            </div>
          )}

          {displayedBadges.length > 0 && (
          <div className="grid grid-cols-3 gap-x-2 gap-y-5 rounded-3xl border border-white/[0.06] bg-black/10 px-3 py-5">
            {displayedBadges.map((badge) => {
              const unlocked = earnedIds.has(badge.id);
              return (
                <div key={badge.id} className="flex min-w-0 flex-col items-center text-center">
                  <MilestoneOrb category={badge.category} unlocked={unlocked} size={48} />
                  <p className={`mt-2 text-[0.625rem] font-semibold leading-tight ${unlocked ? 'text-indigo-50/90' : 'text-white/65'}`}>{badge.title}</p>
                </div>
              );
            })}
          </div>
          )}
          <button
            type="button"
            onClick={() => setShowAllMilestones((shown) => !shown)}
            className="mt-3 w-full rounded-xl border border-white/10 bg-white/[0.035] py-2.5 text-xs font-semibold text-indigo-100/65 hover:bg-white/[0.07]"
          >
            {showAllMilestones ? 'Show less' : `Explore all ${visibleBadges.length} milestones`}
          </button>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onResetSession}
            className="flex-1 py-2.5 text-white/80 text-xs font-bold rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 uppercase tracking-widest transition-colors"
          >
            Reset session
          </button>
          <button
            onClick={onResetLifetime}
            className="flex-1 py-2.5 text-red-200 text-xs font-bold rounded-xl bg-red-500/15 hover:bg-red-500/25 border border-red-500/20 uppercase tracking-widest transition-colors"
          >
            Reset all time
          </button>
        </div>
          </>
        )}

        <button
          onClick={onClose}
          className="w-full mt-3 py-3 text-white font-bold rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 transition-all transform active:scale-95 shadow-lg shadow-purple-500/25"
        >
          Close
        </button>
      </div>
    </Dialog>
  );
};

export default StatsScreen;
